/**
 * 配信状況パネルコンポーネント
 *
 * 顧客詳細画面に埋め込まれる配信状況表示UI。
 * pending件数・最近の配信レコード一覧を表示。
 */

import { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Paper,
  Text,
  Group,
  Loader,
  Title,
  Badge,
  Table,
} from "@mantine/core";
import { showError } from "../../utils/notifications";
import {
  DeliveryItem,
  fetchDeliveryStatus,
} from "../../services/deliveryService";
import { Truck } from "lucide-react";

interface DeliveryStatusPanelProps {
  customerId: string;
}

/**
 * SerializedTimestamp を日本語の日時文字列に変換
 */
function formatTimestamp(ts: { _seconds: number } | null): string {
  if (!ts) return "—";
  return new Date(ts._seconds * 1000).toLocaleString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function DeliveryStatusPanel({ customerId }: DeliveryStatusPanelProps) {
  const [pendingCount, setPendingCount] = useState(0);
  const [deliveries, setDeliveries] = useState<DeliveryItem[]>([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const status = await fetchDeliveryStatus(customerId);
      setPendingCount(status.pendingCount);
      setDeliveries(status.deliveries);
    } catch (error) {
      console.error(error);
      showError("配信状況の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <Paper shadow="xs" p="md" withBorder>
      <Group justify="space-between" mb="md" align="center">
        <Group gap="xs">
          <Truck size={18} />
          <Title order={5}>配信状況</Title>
        </Group>
        <Badge
          size="lg"
          variant={pendingCount > 0 ? "filled" : "light"}
          color={pendingCount > 0 ? "orange" : "gray"}
        >
          未取得: {pendingCount}件
        </Badge>
      </Group>

      {loading && deliveries.length === 0 ? (
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      ) : deliveries.length === 0 ? (
        <Text c="dimmed" ta="center" py="md" size="sm">
          配信レコードがありません
        </Text>
      ) : (
        <Stack gap="xs">
          <Table
            striped
            highlightOnHover
            withTableBorder
            withColumnBorders
            fz="sm"
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>ファイル名</Table.Th>
                <Table.Th w={80} ta="center">ステータス</Table.Th>
                <Table.Th w={150}>作成日時</Table.Th>
                <Table.Th w={150}>取得日時</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {deliveries.map((d) => (
                <Table.Tr key={d.id}>
                  <Table.Td>
                    <Text size="sm" lineClamp={1} title={d.filename}>
                      {d.filename}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="center">
                    <Badge
                      size="xs"
                      variant="light"
                      color={d.status === "pending" ? "orange" : d.status === "downloaded" ? "green" : "red"}
                    >
                      {{ pending: "未取得", downloaded: "取得済", missing: "ファイル欠損", deleting: "期限削除中", expired: "期限削除済" }[d.status]}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {formatTimestamp(d.createdAt)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {formatTimestamp(d.downloadedAt)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          {deliveries.length >= 20 && (
            <Text size="xs" c="dimmed" ta="center">
              最新20件を表示しています
            </Text>
          )}
        </Stack>
      )}
    </Paper>
  );
}
